import { type ForwardedRef, forwardRef, type SVGProps } from 'react';
import {
  type CheckboxRenderProps,
  type SlotProps,
  useSlottedContext,
} from 'react-aria-components';

import { IconContext } from '@infinex/ui-icons/context';
import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useContextProps } from '@infinex/ui-styles';
import { usePrevious } from '@infinex/ui-utils/react';

export type CheckboxPrimitiveProps = {
  /**
   * The visual prominence of the checkbox.
   * @default default
   */
  prominence?: 'default' | 'low';
  /**
   * The size of the checkbox. Use "default" for most cases, the "small" size is
   * reserved for compact contexts like multi-select menus.
   * @default default
   */
  size?: 'default' | 'small';
} & Partial<CheckboxRenderProps> &
  UnsafeStyleProps;

/**
 * @private
 * Render something that looks like a checkbox, without the `<input/>`
 * semantics.
 */
export function CheckboxPrimitive(props: Partial<CheckboxPrimitiveProps>) {
  const {
    className,
    isDisabled,
    isFocusVisible,
    isHovered,
    isIndeterminate,
    isInvalid,
    isPressed,
    isReadOnly,
    isSelected,
    prominence = 'default',
    size = 'default',
    style,
  } = props;

  // Common state for checked + partially checked
  const isChecked = isSelected || isIndeterminate;

  // Keep track of state to avoid a brief flash of the checkmark icon when
  // transitioning from indeterminate to unselected.
  const prevIndeterminate = usePrevious(isIndeterminate);
  const Icon =
    isIndeterminate || (prevIndeterminate && !isSelected)
      ? DashIcon
      : SelectedTick;

  return (
    <div
      role="presentation"
      className={cn(
        'flex items-center justify-center border-2 text-transparent outline-hidden',
        'motion-safe:transition-all motion-safe:ease-out',
        'size-(--size) rounded-(--radius)',
        '[--radius:6px] [--size:20px]',
        {
          '[--size:16px]': size === 'small',
          '[--radius:4px]': size === 'small',
        },
        {
          'bg-fill': prominence === 'default',
        },
        {
          'bg-fillHover border-emphasis': isHovered,
          'bg-fillPress': isPressed,
          'outline-focusRing': isFocusVisible,
        },
        { 'border outline-1': isReadOnly },
        { 'bg-fillDisabled border-transparent': isDisabled },
        {
          'border-brand text-inverse border-[calc(var(--size)/2)]': isChecked,
          'border-inverse': isChecked && prominence === 'low',
          'border-disabled': (isDisabled || isReadOnly) && isChecked,
          // FIXME: use tokens when `cantaloupe` scale is fixed
          'brightness-125': isHovered && isChecked,
          'brightness-95': isPressed && isChecked,
        },
        {
          'border-criticalEmphasis': isInvalid,
        },
        className
      )}
      style={style}
    >
      {Icon && (
        <Icon
          className={cn(
            'size-[calc(var(--size)-6px)]',
            'shrink-0 scale-0 opacity-0',
            'motion-safe:transition-all motion-safe:ease-out',
            { 'scale-100 opacity-100': isChecked },
            { 'size-[calc(var(--size)-4px)]': size === 'small' }
          )}
        />
      )}
    </div>
  );
}

// Icons
// ----------------------------------------------------------------------------

/**
 * @private
 * Used by misc. library components for consistent selection indicators.
 */
// The library's default `CheckIcon` doesn't convey enough “weight”, especially
// at small sizes.
export function SelectedTick(props: SvgProps) {
  return (
    <Svg {...props}>
      <line x1="2" y1="9" x2="6" y2="13" vectorEffect="non-scaling-stroke" />
      <line x1="6" y1="13" x2="14" y2="3" vectorEffect="non-scaling-stroke" />
    </Svg>
  );
}

// Simple dash/hyphen SVG to match the above checkmark, for consistency.
function DashIcon(props: SvgProps) {
  return (
    <Svg {...props}>
      <line x1="2" y1="8" x2="14" y2="8" vectorEffect="non-scaling-stroke" />
    </Svg>
  );
}

type SvgProps = {
  /** Whether the element should be hidden, within a slotted component. */
  isHidden?: boolean;
} & SlotProps &
  SVGProps<SVGSVGElement>;

/** Reusable SVG wrapper for indicators. */
const Svg = forwardRef(function Svg(
  props: SvgProps,
  ref: ForwardedRef<SVGSVGElement>
) {
  [props, ref] = useContextProps(props, ref, IconContext);
  const ctx = useSlottedContext(IconContext);
  const svg = (
    <svg
      aria-hidden="true"
      fill="none"
      focusable="false"
      role="img"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth={2}
      viewBox="0 0 16 16"
      {...props}
      // @ts-expect-error — arbitrary attribute on SVG
      slot={props.slot || 'icon'}
    />
  );

  if (props.isHidden) {
    return null;
  }

  if (ctx?.render) {
    return ctx.render(svg);
  }

  return svg;
});
